import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarBlank, Users, CurrencyDollar, CircleNotch, Trophy } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { PointsKeyPanel } from "@/components/PointsKey";
import { toast } from "sonner";
import { addAllGamesToCalendar } from "@/lib/calendar";

const STATUS_STYLE = {
  draft: "bg-muted text-muted-foreground",
  entries_open: "bg-primary/20 text-primary border border-primary/40",
  live: "bg-kinetic/20 text-kinetic border border-kinetic/40",
  completed: "bg-[#34C759]/20 text-[#34C759] border border-[#34C759]/40",
};
const STATUS_LABEL = { draft: "Draft", entries_open: "Entries Open", live: "Live", completed: "Completed" };

function ListHeader({ scope, setScope }) {
  return (
    <div className="mb-6">
      <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tighter">Tournaments</h1>
      <p className="text-muted-foreground mt-1">Live standings, upcoming events and open entries.</p>
      <div className="inline-flex mt-4 border border-border rounded-sm p-1 bg-card">
        {[["active", "Active"], ["archived", "Past Events"]].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setScope(k)}
            data-testid={`scope-${k}`}
            className={`px-4 py-1.5 rounded-sm font-heading font-bold uppercase tracking-widest text-xs transition-colors ${
              scope === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Tournaments() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tournaments, setTournaments] = useState(null);
  const [busy, setBusy] = useState(null);
  const [scope, setScope] = useState("active");

  const load = async () => {
    const { data } = await api.get(`/tournaments?scope=${scope}`);
    setTournaments(data);
  };

  useEffect(() => {
    setTournaments(null);
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [scope]);

  const join = async (e, t) => {
    e.stopPropagation();
    setBusy(t.id);
    try {
      await api.post(`/tournaments/${t.id}/join`);
      if (t.requires_approval) {
        toast.success("Entry request sent — awaiting admin approval.");
      } else {
        const hasDates = (t.games || []).some((g) => g.scheduled_at);
        if (hasDates) {
          toast.success("You're in! Add the games to your calendar?", {
            action: { label: "Add to calendar", onClick: () => addAllGamesToCalendar(t, t.games) },
            duration: 8000,
          });
        } else {
          toast.success("You're in! Entry recorded.");
        }
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  if (tournaments === null) {
    return (
      <div className="animate-fade-up">
        <ListHeader scope={scope} setScope={setScope} />
        <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-primary" /></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-up">
      <ListHeader scope={scope} setScope={setScope} />

      {tournaments.length === 0 && (
        <div className="border border-border rounded-sm bg-card p-10 text-center">
          <p className="text-muted-foreground">{scope === "archived" ? "No past events yet." : "No tournaments yet."}</p>
          {scope === "active" && user?.role !== "player" && (
            <button onClick={() => navigate("/admin")} className="text-primary font-bold mt-2" data-testid="empty-create-link">Create one in Admin →</button>
          )}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        {tournaments.map((t) => (
          <div
            key={t.id}
            onClick={() => navigate(`/tournament/${t.id}`)}
            data-testid={`tournament-card-${t.id}`}
            className="border border-border bg-card rounded-sm p-5 cursor-pointer hover:bg-[#1E1E1E] hover:border-primary/40 transition-colors group"
          >
            <div className="flex items-start justify-between gap-3">
              <h2 className="font-heading font-bold text-2xl uppercase tracking-tight leading-none group-hover:text-primary transition-colors">{t.name}</h2>
              <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm shrink-0 ${STATUS_STYLE[t.status]}`}>
                {STATUS_LABEL[t.status]}
              </span>
            </div>
            {t.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{t.description}</p>}
            {t.leaders && t.leaders.length > 0 && (
              <div className="mt-2 flex items-center gap-2 text-sm flex-wrap" data-testid={`leader-line-${t.id}`}>
                {t.status === "completed" ? (
                  <span className="flex items-center gap-1 text-[#FFD60A] font-bold uppercase tracking-widest text-xs shrink-0">
                    <Trophy size={16} weight="fill" />{t.leaders.length > 1 ? "Winners" : "Winner"}
                  </span>
                ) : (
                  <span className="text-muted-foreground font-bold uppercase tracking-widest text-xs shrink-0">
                    {t.leaders.length > 1 ? "Leaders" : "Leader"}
                  </span>
                )}
                {t.leaders.map((ldr, i) => (
                  <span key={i} className="flex items-center gap-1.5">
                    <Avatar name={ldr.name} path={ldr.avatar_path} size={22} enlargeable />
                    <span className={`font-semibold ${t.status === "completed" ? "text-[#FFD60A]" : ""}`}>{ldr.name}</span>
                  </span>
                ))}
                <span className="text-muted-foreground">· {t.leader_points}pts</span>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><CalendarBlank size={16} weight="bold" />{t.games?.length || 0} games</span>
              <span className="flex items-center gap-1.5"><Users size={16} weight="bold" />
                {t.max_entries > 0 ? `${t.spots_taken ?? t.entry_count}/${t.max_entries}` : `${t.entry_count}`} entered
              </span>
              {t.entry_fee > 0 && <span className="flex items-center gap-1.5"><CurrencyDollar size={16} weight="bold" />${t.entry_fee} buy-in</span>}
            </div>
            {(t.status === "entries_open" || t.status === "live") && (
              <div className="mt-4">
                {t.joined ? (
                  <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#34C759]" data-testid={`joined-badge-${t.id}`}>✓ You're entered</span>
                ) : t.max_entries > 0 && (t.spots_taken ?? 0) >= t.max_entries ? (
                  <span className="inline-block text-xs font-bold uppercase tracking-widest text-kinetic" data-testid={`full-badge-${t.id}`}>Tournament full</span>
                ) : (
                  <button
                    onClick={(e) => join(e, t)}
                    disabled={busy === t.id}
                    data-testid={`join-button-${t.id}`}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-bold uppercase tracking-widest text-sm px-4 py-2 rounded-sm transition-colors"
                  >
                    {busy === t.id ? "Joining…" : t.requires_approval ? "Request Entry" : "Enter Tournament"}
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {scope === "active" && (
        <div className="mt-8">
          <h2 className="font-heading font-bold text-lg uppercase tracking-wide text-muted-foreground mb-2">Scoring</h2>
          <PointsKeyPanel defaultOpen />
        </div>
      )}
    </div>
  );
}
